import { Stack } from '@mui/material'
import { isEmpty } from 'lodash'
import { AreaValue } from 'ComponentLibrary/src/common/area'
import { MonetaryValue, type MonetaryValueDataObjectType, type MoneyPerAreaValue } from 'ComponentLibrary/src/common/money'
import { decimalToLocalizedString } from 'ComponentLibrary/src/common/numbers'
import { Percentage } from 'ComponentLibrary/src/common/percentage'
import { Typography } from 'ComponentLibrary/src/piazza/text'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { getLeadingValuePerSqm, type LeadingValuePerSqmLabels, type LeadingValuePerSqmValuation } from './getLeadingValuePerSqm'

type ValueByValuationMethod = Partial<
    Record<'land_value' | 'income' | 'investment_method' | 'cost' | 'comparison' | 'discounted_cashflow', MonetaryValueDataObjectType>
>

type TenantSummaryTotal = {
    gross_rental_income: { total: MonetaryValueDataObjectType }
    actual_rental_income: { total: MonetaryValueDataObjectType }
    management_cost: { total: MonetaryValueDataObjectType }
    net_rental_income: { total: MonetaryValueDataObjectType }
}

export type AllValuesValuation = LeadingValuePerSqmValuation & {
    market_value_with_trend: { value: MonetaryValueDataObjectType }
    market_value_by_valuation_method: ValueByValuationMethod
    investment_method_market_value?: MonetaryValueDataObjectType
    transaction_costs?: MonetaryValueDataObjectType
    market_value_including_transaction_costs: MonetaryValueDataObjectType
    mortgage_lending_value?: MonetaryValueDataObjectType
    mortgage_lending_value_by_valuation_method: ValueByValuationMethod
    tenant_summary?: { total?: TenantSummaryTotal }
    wault?: number | 'unlimited'
    gross_yield_multiplier?: number
    net_yield_multiplier?: number
    gross_yield?: number
    gross_initial_yield?: number
    discount_rate?: number
    economic_units: Array<{
        structures: Array<{
            title: string
            life_span: {
                custom_residual?: number
                calculated_residual?: number
            }
        }>
    }>
}

export type AllValuesCopy = {
    title: string
    sections: {
        marketValueResults: string
        mortgageLendingValueResults: string
        incomeMetrics: string
        yields: string
        relevantArea: string
        residualUsefulLife: string
    }
    rows: {
        marketValue: string
        landValue: string
        incomeMethod: string
        investmentMethod: string
        costMethod: string
        comparisonMethod: string
        discountedCashflow: string
        transactionCosts: string
        marketValueIncludingTransactionCosts: string
        mortgageLendingValue: string
        grossRentalIncome: string
        contractRent: string
        marketRent: string
        managementCosts: string
        netRentalIncome: string
        wault: string
        grossYieldMultiplier: string
        netYieldMultiplier: string
        grossYield: string
        grossInitialYield: string
        discountRate: string
        rentableArea: string
        grossFloorArea: string
        landArea: string
    }
    values: {
        waultUnlimited: string
    }
    units: {
        year: string
        years: string
    }
    leadingValuePerSqm: LeadingValuePerSqmLabels
}

export type AllValuesProps = {
    valuation: AllValuesValuation
    copy: AllValuesCopy
    locale: string
}

export function AllValues({ valuation, copy, locale }: AllValuesProps) {
    const { valuePerSqmValue, valuePerSqmLabel } = getLeadingValuePerSqm(valuation, copy.leadingValuePerSqm)
    const allStructures = valuation.economic_units.flatMap((economicUnit) => economicUnit.structures)

    return (
        <Stack direction="column" width="100%" gap={tokens.SizingSpacing300}>
            <Headline variant="headline3" title={copy.title} />
            <Stack direction="row" width="100%" gap={tokens.SizingSpacing300} flexWrap="wrap">
                <Stack direction="column" width={`calc(50% - ${tokens.SizingSpacing150})`} gap={tokens.SizingSpacing100}>
                    <Headline variant="headline4" title={copy.sections.marketValueResults} />
                    <Stack direction="column" gap={tokens.SizingSpacing050}>
                        <MonetaryDetail label={copy.rows.marketValue} value={valuation.market_value_with_trend.value} locale={locale} />
                        {valuation.selected_valuation_methods.includes('land_value') && (
                            <MonetaryDetail
                                label={copy.rows.landValue}
                                value={valuation.market_value_by_valuation_method.land_value}
                                locale={locale}
                            />
                        )}
                        {valuation.selected_valuation_methods.includes('income') && (
                            <MonetaryDetail
                                label={copy.rows.incomeMethod}
                                value={valuation.market_value_by_valuation_method.income}
                                locale={locale}
                            />
                        )}
                        {valuation.selected_valuation_methods.includes('investment_method') && (
                            <MonetaryDetail
                                label={copy.rows.investmentMethod}
                                value={valuation.investment_method_market_value}
                                locale={locale}
                            />
                        )}
                        {valuation.selected_valuation_methods.includes('cost') && (
                            <MonetaryDetail
                                label={copy.rows.costMethod}
                                value={valuation.market_value_by_valuation_method.cost}
                                locale={locale}
                            />
                        )}
                        {valuation.selected_valuation_methods.includes('comparison') && (
                            <MonetaryDetail
                                label={copy.rows.comparisonMethod}
                                value={valuation.market_value_by_valuation_method.comparison}
                                locale={locale}
                            />
                        )}
                        {valuation.selected_valuation_methods.includes('discounted_cashflow') && (
                            <MonetaryDetail
                                label={copy.rows.discountedCashflow}
                                value={valuation.market_value_by_valuation_method.discounted_cashflow}
                                locale={locale}
                            />
                        )}
                        <MonetaryDetail label={copy.rows.transactionCosts} value={valuation.transaction_costs} locale={locale} />
                        <MonetaryDetail
                            label={copy.rows.marketValueIncludingTransactionCosts}
                            value={valuation.market_value_including_transaction_costs}
                            locale={locale}
                        />
                        <MonetaryDetail label={valuePerSqmLabel} value={valuePerSqmValue} locale={locale} />
                    </Stack>
                </Stack>
                {valuation.mortgage_lending_value && (
                    <Stack direction="column" width={`calc(50% - ${tokens.SizingSpacing150})`} gap={tokens.SizingSpacing100}>
                        <Headline variant="headline4" title={copy.sections.mortgageLendingValueResults} />
                        <Stack direction="column" gap={tokens.SizingSpacing050}>
                            <MonetaryDetail
                                label={copy.rows.mortgageLendingValue}
                                value={valuation.mortgage_lending_value}
                                locale={locale}
                            />
                            {valuation.selected_valuation_methods.includes('land_value') && (
                                <MonetaryDetail
                                    label={copy.rows.landValue}
                                    value={valuation.mortgage_lending_value_by_valuation_method.land_value}
                                    locale={locale}
                                />
                            )}
                            {valuation.selected_valuation_methods.includes('income') && (
                                <MonetaryDetail
                                    label={copy.rows.incomeMethod}
                                    value={valuation.mortgage_lending_value_by_valuation_method.income}
                                    locale={locale}
                                />
                            )}
                            {valuation.selected_valuation_methods.includes('cost') && (
                                <MonetaryDetail
                                    label={copy.rows.costMethod}
                                    value={valuation.mortgage_lending_value_by_valuation_method.cost}
                                    locale={locale}
                                />
                            )}
                            {valuation.selected_valuation_methods.includes('comparison') && (
                                <MonetaryDetail
                                    label={copy.rows.comparisonMethod}
                                    value={valuation.mortgage_lending_value_by_valuation_method.comparison}
                                    locale={locale}
                                />
                            )}
                        </Stack>
                    </Stack>
                )}
                {valuation.tenant_summary && (
                    <Stack direction="column" width={`calc(50% - ${tokens.SizingSpacing150})`} gap={tokens.SizingSpacing100}>
                        <Headline variant="headline4" title={copy.sections.incomeMetrics} />
                        <Stack direction="column" gap={tokens.SizingSpacing050}>
                            <MonetaryDetail
                                label={copy.rows.grossRentalIncome}
                                value={valuation.tenant_summary.total?.gross_rental_income.total}
                                locale={locale}
                            />
                            <MonetaryDetail
                                label={copy.rows.contractRent}
                                value={valuation.tenant_summary.total?.actual_rental_income.total}
                                locale={locale}
                            />
                            <MonetaryDetail
                                label={copy.rows.marketRent}
                                value={valuation.tenant_summary.total?.gross_rental_income.total}
                                locale={locale}
                            />
                            <MonetaryDetail
                                label={copy.rows.managementCosts}
                                value={valuation.tenant_summary.total?.management_cost.total}
                                locale={locale}
                            />
                            <MonetaryDetail
                                label={copy.rows.netRentalIncome}
                                value={valuation.tenant_summary.total?.net_rental_income.total}
                                locale={locale}
                            />
                            {valuation.wault === 'unlimited' ? (
                                <Detail label={copy.rows.wault} value={copy.values.waultUnlimited} />
                            ) : (
                                <YearDetail
                                    label={copy.rows.wault}
                                    value={valuation.wault}
                                    allowDecimalYear
                                    unitSingle={copy.units.year}
                                    unitPlural={copy.units.years}
                                    locale={locale}
                                />
                            )}
                        </Stack>
                    </Stack>
                )}
                {valuation.selected_valuation_methods.includes('income') && (
                    <Stack direction="column" width={`calc(50% - ${tokens.SizingSpacing150})`} gap={tokens.SizingSpacing100}>
                        <Headline variant="headline4" title={copy.sections.yields} />
                        <Stack direction="column" gap={tokens.SizingSpacing050}>
                            <NumberDetail label={copy.rows.grossYieldMultiplier} value={valuation.gross_yield_multiplier} locale={locale} />
                            <NumberDetail label={copy.rows.netYieldMultiplier} value={valuation.net_yield_multiplier} locale={locale} />
                            <PercentageDetail label={copy.rows.grossYield} value={valuation.gross_yield} locale={locale} />
                            <PercentageDetail label={copy.rows.grossInitialYield} value={valuation.gross_initial_yield} locale={locale} />
                            <PercentageDetail label={copy.rows.discountRate} value={valuation.discount_rate} locale={locale} />
                        </Stack>
                    </Stack>
                )}
                <Stack direction="column" width={`calc(50% - ${tokens.SizingSpacing150})`} gap={tokens.SizingSpacing100}>
                    <Headline variant="headline4" title={copy.sections.relevantArea} />
                    <Stack direction="column" gap={tokens.SizingSpacing050}>
                        <AreaDetail label={copy.rows.rentableArea} value={valuation.rentable_area?.value} locale={locale} />
                        <AreaDetail label={copy.rows.grossFloorArea} value={valuation.gross_floor_area?.value} locale={locale} />
                        <AreaDetail label={copy.rows.landArea} value={valuation.land_area?.value} locale={locale} />
                    </Stack>
                </Stack>
                {!isEmpty(allStructures) && (
                    <Stack direction="column" width={`calc(50% - ${tokens.SizingSpacing150})`} gap={tokens.SizingSpacing100}>
                        <Headline variant="headline4" title={copy.sections.residualUsefulLife} />
                        <Stack direction="column" gap={tokens.SizingSpacing050}>
                            {allStructures.map((structure) => (
                                <YearDetail
                                    key={structure.title}
                                    label={structure.title}
                                    value={structure.life_span.custom_residual ?? structure.life_span.calculated_residual}
                                    unitSingle={copy.units.year}
                                    unitPlural={copy.units.years}
                                    locale={locale}
                                />
                            ))}
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}

interface DetailProps<T> {
    label: string
    value?: T
    unit?: string
}

type LocalizedDetailProps<T> = DetailProps<T> & {
    locale: string
}

function Detail({ label, value = '-', unit = '' }: DetailProps<string>) {
    return (
        <Stack width="100%" direction="row" justifyContent="space-between">
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {label}
            </Typography>
            <Stack direction="row" gap={tokens.SizingSpacing075}>
                <Typography variant="textS" color={tokens.ColorTextBold}>
                    {value}
                </Typography>
                {unit && (
                    <Typography variant="textS" color={tokens.ColorTextSubtle}>
                        {unit}
                    </Typography>
                )}
            </Stack>
        </Stack>
    )
}

function AreaDetail({ label, value, locale }: LocalizedDetailProps<number>) {
    return (
        <Detail
            label={label}
            unit="m²"
            value={formatDetailValue(value, (areaValue) => new AreaValue(areaValue).format({ withUnit: false, locale }))}
        />
    )
}

function YearDetail({
    label,
    value,
    allowDecimalYear,
    unitSingle,
    unitPlural,
    locale,
}: LocalizedDetailProps<number> & { allowDecimalYear?: boolean; unitSingle: string; unitPlural: string }) {
    const unit = value === 1 ? unitSingle : unitPlural
    return (
        <Detail
            label={label}
            value={formatDetailValue(value, (yearValue) =>
                decimalToLocalizedString(yearValue, false, allowDecimalYear ? 2 : 1, undefined, locale)
            )}
            unit={value === undefined ? undefined : unit}
        />
    )
}

function MonetaryDetail({ label, value, locale }: LocalizedDetailProps<MonetaryValueDataObjectType | MoneyPerAreaValue>) {
    return (
        <Detail
            label={label}
            unit="€"
            value={formatDetailValue(value, (monetaryValue) =>
                MonetaryValue.fromMonetaryValueDataObject(monetaryValue).format({ withoutCurrency: true, locale })
            )}
        />
    )
}

function PercentageDetail({ label, value, locale }: LocalizedDetailProps<number>) {
    return (
        <Detail
            label={label}
            unit="%"
            value={formatDetailValue(value, (percentageValue) => new Percentage(percentageValue).format({ withUnit: false, locale }))}
        />
    )
}

function NumberDetail({ label, value, locale }: LocalizedDetailProps<number>) {
    return (
        <Detail
            label={label}
            value={formatDetailValue(value, (numberValue) => decimalToLocalizedString(numberValue, false, 2, undefined, locale))}
        />
    )
}

function formatDetailValue<T>(value: T | undefined, formatter: (value: T) => string): string | undefined {
    return value !== undefined ? formatter(value) : undefined
}
